import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Package, Clock, TrendingUp, PieChart, CalendarCheck, ArrowRight, Users, CheckSquare, ArrowUpDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { isAdmin as isAdminRole } from '@/lib/roles';
import { cn, formatCurrency } from '@/lib/utils';
import { fetchBuildTimeSummary } from '@/lib/api/buildTimeStats';
import { BuildTimeSummaryValue } from '@/components/BuildDuration';
import { creditedUnits } from '@/lib/teamwork';

const SKU_FAMILY: Record<string, string> = {
  Z: 'Zero', '1': 'One', P: 'Pro', M: 'Master', L: 'Legend', E: 'Elite', C: 'Custom',
};
const getFamilyFromSku = (sku: string): string => {
  if (!sku) return 'Unknown';
  const prefix = sku.startsWith('AOI-') ? sku[4] : sku[0];
  return SKU_FAMILY[prefix] ?? 'Unknown';
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  picking: 'bg-orange-100 text-orange-800',
  building: 'bg-blue-100 text-blue-800',
  testing: 'bg-purple-100 text-purple-800',
  awaiting_review: 'bg-pink-100 text-pink-800',
  in_progress: 'bg-purple-100 text-purple-800',
};

const COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
  'bg-orange-500', 'bg-pink-500', 'bg-cyan-500',
];

type StatusFilter = 'all' | 'active' | 'inactive';
type PaoSortField = 'order_number' | 'completed_date' | 'build_sku' | 'quantity' | 'days_to_build' | 'days_to_process' | 'total_cost_ex_vat';
type SortOrder = 'asc' | 'desc';

interface TechnicianOption {
  id: string;
  name: string;
  initials: string;
  is_active: boolean;
}

export function ManagerDashboardPage() {
  const [selectedTechnician, setSelectedTechnician] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [dateRange, setDateRange] = useState({
    from: '2022-01-01',
    to: new Date().toISOString().split('T')[0],
  });

  // Load the current user's own record (including role)
  const { data: me } = useQuery({
    queryKey: ['my-manager-record'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data, error } = await supabase
        .from('technicians')
        .select('id, name, role')
        .eq('user_id', user.id)
        .single();
      if (error) { console.error('Error fetching manager record:', error); return null; }
      return data as { id: string; name: string; role: string };
    },
  });

  const isAdmin = isAdminRole(me?.role);

  // Fetch technicians — admins see all, managers see only their managed techs
  const { data: allManaged } = useQuery({
    queryKey: ['all-managed-technicians', me?.id, isAdmin],
    queryFn: async () => {
      if (!me?.id) return [];
      let query = supabase
        .from('technicians')
        .select('id, name, initials, is_active')
        .in('role', ['technician', 'admin'])
        .order('name');
      if (!isAdmin) query = query.eq('managed_by', me.id);
      const { data, error } = await query;
      if (error) return [];
      return data as TechnicianOption[];
    },
    enabled: !!me?.id,
  });

  // Technicians shown in the dropdown when filtering by status
  const listByStatus = (allManaged || []).filter(t => {
    if (statusFilter === 'active') return t.is_active;
    if (statusFilter === 'inactive') return !t.is_active;
    return true;
  });

  // Whether we need to include orders with no technician assigned
  const includeUnassigned = selectedTechnician === 'unassigned' || selectedTechnician === 'all';

  // Resolve which technicians' IDs to show
  const resolveIds = (): string[] => {
    if (selectedTechnician === 'unassigned') return [];
    if (selectedTechnician !== 'all') return [selectedTechnician];
    return listByStatus.map(t => t.id);
  };

  const technicianIds = resolveIds();
  const hasSelection = technicianIds.length > 0 || includeUnassigned;

  // Helper: apply technician filter to a query, handling null technician_id
  const applyTechFilter = (query: any) => {
    if (selectedTechnician === 'unassigned') {
      return query.is('technician_id', null);
    }
    if (selectedTechnician !== 'all') {
      return query.eq('technician_id', selectedTechnician);
    }
    // "all" — admins with no status filter see everything; otherwise filter to listed techs + unassigned
    if (isAdmin && statusFilter === 'all') return query;
    if (technicianIds.length > 0) {
      const ids = technicianIds.map(id => `technician_id.eq.${id}`).join(',');
      return query.or(`${ids},technician_id.is.null`);
    }
    return query.is('technician_id', null);
  };

  // Completed work is credited per unit to whoever built it
  // (assembly_orders.builder_ids / unit_builders), not to the assignee: on a
  // team order the units can be built by different technicians.
  const applyBuilderFilter = (query: any) => {
    if (selectedTechnician === 'unassigned') return query.is('technician_id', null);
    if (selectedTechnician !== 'all') return query.contains('builder_ids', [selectedTechnician]);
    if (isAdmin && statusFilter === 'all') return query;
    if (technicianIds.length > 0) {
      return query.or(`builder_ids.ov.{${technicianIds.join(',')}},technician_id.is.null`);
    }
    return query.is('technician_id', null);
  };
  // The technicians whose units count; null = every unit.
  const creditIds: string[] | null =
    selectedTechnician === 'unassigned' ? null
      : selectedTechnician !== 'all' ? [selectedTechnician]
      : isAdmin && statusFilter === 'all' ? null
      : technicianIds;
  const unitsFor = (o: any) =>
    !o.technician_id && !(o.builder_ids?.length) ? Math.max(o.quantity ?? 1, 1) : creditedUnits(o, creditIds);

  // PAO stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['manager-pao-stats', technicianIds, includeUnassigned, dateRange, selectedTechnician, statusFilter],
    queryFn: async () => {
      if (!hasSelection) return { totalUnits: 0, unitsThisWeek: 0, familyData: [] };

      let query = supabase
        .from('assembly_orders')
        .select('id, build_sku, quantity, completed_date, technician_id, unit_builders, builder_ids')
        .gte('completed_date', dateRange.from)
        .lte('completed_date', dateRange.to);

      query = applyBuilderFilter(query);

      const { data: paoData, error } = await query;
      if (error) { console.error('PAO query error:', error); return { totalUnits: 0, unitsThisWeek: 0, familyData: [] }; }
      if (!paoData || paoData.length === 0) return { totalUnits: 0, unitsThisWeek: 0, familyData: [] };

      const totalUnits = paoData.reduce((sum, p: any) => sum + unitsFor(p), 0);
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const unitsThisWeek = paoData
        .filter(p => new Date(p.completed_date) >= oneWeekAgo)
        .reduce((sum, p: any) => sum + unitsFor(p), 0);

      const familyBreakdown: Record<string, number> = {};
      paoData.forEach((p: any) => {
        const units = unitsFor(p);
        if (!units) return;
        const family = getFamilyFromSku(p.build_sku || '');
        familyBreakdown[family] = (familyBreakdown[family] || 0) + units;
      });
      const familyData = Object.entries(familyBreakdown)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      return { totalUnits, unitsThisWeek, familyData };
    },
    enabled: hasSelection,
  });

  // Typical build time: median, measured as on each order's detail page
  const { data: buildTime } = useQuery({
    queryKey: ['manager-build-time', technicianIds, includeUnassigned, dateRange, selectedTechnician, statusFilter],
    queryFn: () => fetchBuildTimeSummary(applyBuilderFilter, dateRange.from, dateRange.to),
    enabled: hasSelection,
  });

  // Team's active assembly orders
  const { data: teamOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ['manager-team-orders', technicianIds, includeUnassigned],
    queryFn: async () => {
      if (!hasSelection) return { count: 0, items: [] };
      let query = supabase
        .from('assembly_orders')
        .select('id, order_number, build_sku, quantity, status, created_at, technician_id', { count: 'exact' })
        .in('status', ['pending', 'picking', 'building', 'testing', 'awaiting_review', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(10);
      query = applyTechFilter(query);
      const { data, error, count } = await query;
      if (error) { console.error('Team orders error:', error); return { count: 0, items: [] }; }
      return { count: count || 0, items: data || [] };
    },
    enabled: hasSelection,
  });

  // Posted assembly orders for managed technicians
  const [paoPage, setPaoPage] = useState(1);
  const paoPageSize = 20;
  const [paoSortField, setPaoSortField] = useState<PaoSortField>('completed_date');
  const [paoSortOrder, setPaoSortOrder] = useState<SortOrder>('desc');

  const handlePaoSort = (field: PaoSortField) => {
    if (paoSortField === field) {
      setPaoSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setPaoSortField(field);
      setPaoSortOrder('asc');
    }
    setPaoPage(1);
  };

  const PaoSortHeader = ({ field, children, className = '' }: { field: PaoSortField; children: React.ReactNode; className?: string }) => (
    <th
      className={cn('text-left p-3 font-medium cursor-pointer hover:bg-muted/70 select-none whitespace-nowrap', className)}
      onClick={() => handlePaoSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${paoSortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
    </th>
  );
  const { data: postedOrders, isLoading: postedLoading } = useQuery({
    queryKey: ['manager-posted-orders', technicianIds, includeUnassigned, dateRange, paoPage, paoSortField, paoSortOrder, selectedTechnician, statusFilter],
    queryFn: async () => {
      if (!hasSelection) return { count: 0, items: [] };
      let query = supabase
        .from('assembly_orders')
        .select('id, order_number, completed_date, build_sku, quantity, technician_id, builder_ids, to_number, days_to_build, days_to_process, total_cost_ex_vat', { count: 'exact' })
        .in('status', ['Posted', 'Completed'])
        .gte('completed_date', dateRange.from)
        .lte('completed_date', dateRange.to)
        .order(paoSortField, { ascending: paoSortOrder === 'asc', nullsFirst: false });
      query = applyBuilderFilter(query);
      const from = (paoPage - 1) * paoPageSize;
      query = query.range(from, from + paoPageSize - 1);
      const { data, error, count } = await query;
      if (error) { console.error('Posted orders error:', error); return { count: 0, items: [] }; }
      return { count: count || 0, items: data || [] };
    },
    enabled: hasSelection,
  });
  const paoTotalPages = Math.ceil((postedOrders?.count ?? 0) / paoPageSize);

  // Subtitle based on current selection
  const getViewLabel = () => {
    if (selectedTechnician === 'unassigned') return 'Unassigned / N/A';
    if (selectedTechnician !== 'all') {
      const tech = allManaged?.find(t => t.id === selectedTechnician);
      return tech ? `${tech.name} (${tech.initials})${!tech.is_active ? ' — inactive' : ''}` : 'Technician';
    }
    if (statusFilter === 'active') return 'All Active Technicians';
    if (statusFilter === 'inactive') return 'All Inactive Technicians';
    return 'All Technicians';
  };

  const activeCount = (allManaged || []).filter(t => t.is_active).length;
  const inactiveCount = (allManaged || []).filter(t => !t.is_active).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Team Dashboard</h1>
        <p className="text-muted-foreground">
          {isAdmin ? 'All technicians · ' : 'Your team · '}Viewing: {getViewLabel()}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {/* Top row: status + technician + count */}
          <div className="flex flex-wrap gap-4 items-center">
            {/* Status segmented buttons */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">Status:</label>
              <div className="flex rounded-md border border-input overflow-hidden">
                {(['all', 'active', 'inactive'] as StatusFilter[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => { setStatusFilter(s); setSelectedTechnician('all'); }}
                    className={cn(
                      'px-3 h-10 text-sm capitalize transition-colors border-r last:border-r-0 border-input',
                      statusFilter === s && selectedTechnician === 'all'
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'bg-background hover:bg-muted'
                    )}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Individual technician picker */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">Technician:</label>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={selectedTechnician}
                onChange={(e) => setSelectedTechnician(e.target.value)}
              >
                <option value="all">
                  {statusFilter === 'active' ? 'All Active' :
                   statusFilter === 'inactive' ? 'All Inactive' :
                   'All Technicians'}
                </option>
                <option value="unassigned">Unassigned / N/A</option>
                {/* Active group */}
                {(statusFilter === 'all' || statusFilter === 'active') &&
                  (allManaged || []).filter(t => t.is_active).length > 0 && (
                  <>
                    {statusFilter === 'all' && <option disabled>── Active ──</option>}
                    {(allManaged || []).filter(t => t.is_active).map(tech => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name} ({tech.initials})
                      </option>
                    ))}
                  </>
                )}
                {/* Inactive group */}
                {(statusFilter === 'all' || statusFilter === 'inactive') &&
                  (allManaged || []).filter(t => !t.is_active).length > 0 && (
                  <>
                    {statusFilter === 'all' && <option disabled>── Inactive ──</option>}
                    {(allManaged || []).filter(t => !t.is_active).map(tech => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name} ({tech.initials}) — inactive
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            {/* Roster summary */}
            {allManaged && allManaged.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
                <Users className="h-3.5 w-3.5" />
                <span>{activeCount} active / {inactiveCount} inactive</span>
              </div>
            )}
          </div>

          {/* Bottom row: date range */}
          <div className="flex flex-wrap gap-4 pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">From:</label>
              <input
                type="date"
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">To:</label>
              <input
                type="date"
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Units Built</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.totalUnits ?? 0}</div>
            <p className="text-xs text-muted-foreground">In selected period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Units Built This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statsLoading ? '...' : stats?.unitsThisWeek ?? 0}</div>
            <p className="text-xs text-muted-foreground">Last 7 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Orders</CardTitle>
            <CalendarCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ordersLoading ? '...' : teamOrders?.count ?? 0}</div>
            <p className="text-xs text-muted-foreground">In progress across team</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Typical Build Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <BuildTimeSummaryValue summary={buildTime} />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Team Active Orders List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5" />
              Active Orders
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/assembly-orders">View All <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </CardHeader>
          <CardContent>
            {ordersLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : teamOrders?.items && teamOrders.items.length > 0 ? (
              <div className="space-y-3">
                {teamOrders.items.map((order: any) => {
                  const tech = allManaged?.find(t => t.id === order.technician_id);
                  return (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate font-mono">{order.build_sku || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {order.order_number}{tech ? ` · ${tech.initials}` : !order.technician_id ? ' · N/A' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <span className="text-sm font-medium">×{order.quantity || 1}</span>
                        <Badge className={cn('text-xs', STATUS_COLORS[order.status] || 'bg-gray-100')}>
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No active orders</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Build Family Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Build Family Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : stats?.familyData && stats.familyData.length > 0 ? (
              <div className="space-y-3">
                {stats.familyData.map((item, index) => {
                  const maxCount = Math.max(...stats.familyData.map(d => d.count));
                  const pct = (item.count / maxCount) * 100;
                  const totalPct = stats.totalUnits > 0 ? (item.count / stats.totalUnits) * 100 : 0;
                  return (
                    <div key={item.name} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium truncate">{item.name}</span>
                        <span className="text-muted-foreground ml-2">{item.count} ({totalPct.toFixed(0)}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${COLORS[index % COLORS.length]} rounded-full transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No data in selected period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Posted Assembly Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5" />
            Posted Assembly Orders
            {postedOrders && postedOrders.count > 0 && (
              <span className="text-sm font-normal text-muted-foreground ml-1">({postedOrders.count} total)</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {postedLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : postedOrders?.items && postedOrders.items.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <PaoSortHeader field="order_number">ASO #</PaoSortHeader>
                      <PaoSortHeader field="completed_date">Date</PaoSortHeader>
                      <PaoSortHeader field="build_sku">Build SKU</PaoSortHeader>
                      <th className="text-left p-3 font-medium whitespace-nowrap">Technician</th>
                      <PaoSortHeader field="quantity" className="text-right p-3">Qty</PaoSortHeader>
                      <PaoSortHeader field="days_to_build" className="text-right p-3">Days to Build</PaoSortHeader>
                      <PaoSortHeader field="days_to_process" className="text-right p-3">Days to Process</PaoSortHeader>
                      <PaoSortHeader field="total_cost_ex_vat" className="text-right p-3">TO</PaoSortHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {postedOrders.items.map((order: any) => {
                      const tech = allManaged?.find(t => t.id === order.technician_id);
                      const builders: string[] = order.builder_ids ?? [];
                      const sharedBuild = builders.length > 1 || (builders.length === 1 && builders[0] !== order.technician_id);
                      const techLabel = sharedBuild
                        ? builders.map(id => allManaged?.find(t => t.id === id)?.initials ?? '?').join(' + ')
                        : tech
                        ? `${tech.name} (${tech.initials})`
                        : order.technician_id ? (order.technician_name || '—') : 'Unassigned';
                      return (
                        <tr key={order.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="p-3 font-mono text-xs font-medium">{order.order_number || '—'}</td>
                          <td className="p-3 text-muted-foreground whitespace-nowrap">
                            {order.completed_date
                              ? new Date(order.completed_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                              : '—'}
                          </td>
                          <td className="p-3 font-mono text-xs">{order.build_sku || '—'}</td>
                          <td className="p-3">{techLabel}</td>
                          <td className="p-3 text-right font-medium">{order.quantity ?? '—'}</td>
                          <td className="p-3 text-right">{order.days_to_build ?? '—'}</td>
                          <td className="p-3 text-right">{order.days_to_process ?? '—'}</td>
                          <td className="p-3 text-right font-mono text-xs whitespace-nowrap">{order.total_cost_ex_vat != null ? formatCurrency(order.total_cost_ex_vat) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {paoTotalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((paoPage - 1) * paoPageSize) + 1}–{Math.min(paoPage * paoPageSize, postedOrders.count)} of {postedOrders.count}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => setPaoPage(p => p - 1)} disabled={paoPage === 1}>Previous</Button>
                    <span className="px-3 text-sm">Page {paoPage} of {paoTotalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPaoPage(p => p + 1)} disabled={paoPage === paoTotalPages}>Next</Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              No posted orders in selected period
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
